import { useContext } from 'react';
import { Card, CardContent, Typography, Divider, Box, useMediaQuery } from '@mui/material';
import { useB3Lang } from '@b3/lang';
import { OrderDetailsContext } from '../context/OrderDetailsContext';

export default function OrderShippingInfo() {
  const b3Lang = useB3Lang();
  const {
    state: { shippingInfo },
  } = useContext(OrderDetailsContext);

  const isSmallScreen = useMediaQuery('(max-width:600px)');

  if (!shippingInfo || Object.keys(shippingInfo).length === 0) {
    return null;
  }

  const renderInfoRow = (label: string, value: string | undefined) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isSmallScreen ? 'column' : 'row',
        justifyContent: 'space-between',
        padding: '10px 14px',
        backgroundColor: value ? '#fff' : '#f5f5f5',
        borderRadius: 1,
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          color: '#555',
          marginBottom: isSmallScreen ? '4px' : 0,
        }}
      >
        {b3Lang(`orderDetail.shippingInfo.${label}`)}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: value ? '#0288D1' : '#999',
          textAlign: isSmallScreen ? 'left' : 'right',
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  );

  return (
    <Card
      sx={{
        borderRadius: 1,
        boxShadow: 4,
        backgroundColor: '#fcfcfc',
        border: '1px solid #ddd',
        padding: 2,
      }}
    >
      <CardContent>
        <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: 2, color: '#333' }}>
          {b3Lang('orderDetail.shippingInfo.title')}
        </Typography>

        <Divider sx={{ marginBottom: 2 }} />

        {renderInfoRow('carrier', shippingInfo.carrier)}
        {renderInfoRow('carrierEmail', shippingInfo.carrierEmail)}
        {renderInfoRow('carrierPhone', shippingInfo.carrierPhone)}

        <Divider sx={{ marginY: 2 }} />

        {(shippingInfo.deliveryItems || []).length > 0 && (
          <>
            <Divider sx={{ marginY: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', marginBottom: 1 }}>
              {b3Lang('orderDetail.shippingInfo.deliveryItemsTitle')}
            </Typography>

            {(shippingInfo.deliveryItems || []).map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
                  gap: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  padding: 2,
                  marginBottom: 2,
                  backgroundColor: '#fafafa',
                }}
              >
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#0288D1' }}>
                    {item.productName}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#999' }}>
                    {b3Lang('orderDetail.shippingInfo.productId')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>
                    {item.productId}
                  </Typography>
                </Box>

                {item.confirmationStatusData && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#999' }}>
                      {b3Lang('orderDetail.shippingInfo.confirmationStatus')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: item.confirmationStatusData.color,
                        fontWeight: 500,
                      }}
                    >
                      {b3Lang(item.confirmationStatusData.translationKey)}
                    </Typography>
                  </Box>
                )}
                {item.confirmedDeliveryDate && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#999' }}>
                      {b3Lang('orderDetail.shippingInfo.confirmedDeliveryDate')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      {`${item.confirmedDeliveryDate} (${item.confirmedQuantity} ${
                        item.confirmedQuantity === 1
                          ? b3Lang('orderDetail.shippingInfo.unitSingular')
                          : b3Lang('orderDetail.shippingInfo.unitPlural')
                      })`}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
        <Box
          sx={{
            marginTop: '50px',
            padding: '15px 14px',
            backgroundColor: '#fff',
            borderRadius: 2,
            textAlign: isSmallScreen ? 'left' : 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#0288D1',
              fontSize: isSmallScreen ? '0.95rem' : '1rem',
            }}
          >
            {shippingInfo.deliveryAddress?.recipientName || '-'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#555',
              fontSize: isSmallScreen ? '0.9rem' : '1rem',
            }}
          >
            {`${shippingInfo.deliveryAddress?.street || '-'}, ${
              shippingInfo.deliveryAddress?.city || '-'
            }, ${shippingInfo.deliveryAddress?.postalCode || '-'}, ${
              shippingInfo.deliveryAddress?.country || '-'
            }`}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
